<template>
    <hts-drawer
        v-model="visible"
        color="main"
        :title="$t('labels.workflowModule.detail')"
        size="large"
        @close="close">
        <div
            v-if="visible"
            :style="{ minWidth: minWidthScroll }">
            <hts-card :title="$t('labels.generalInformation')">
                <hts-form-input
                    v-model="dataEdit.name"
                    :label="$t('labels.workflowModule.name')"
                    name="workflowModule.name"
                    prop="name"
                    readonly />
                <hts-form-select
                    v-model="dataEdit.transportation"
                    :items="transportation"
                    :label="$t('labels.transportation')"
                    name="transportation"
                    prop="transportation"
                    readonly />
            </hts-card>

            <hts-card
                :title="$t('labels.workflow')"
                class="relative">
                <hts-form-button
                    class="mb-4"
                    color="default"
                    readonly
                    :outline="false"
                    icon-name="hi-cog-8-tooth-icon">
                    {{ $t('labels.settingPositionNumber') }}
                </hts-form-button>

                <hts-workflow-position
                    :positions="dataEdit.positions"
                    :is-active="(item) => item.tasks?.length">
                    <template #default="{ item, index }">
                        <div class="w-72 mb-2 px-2 box-border">
                            <hts-admincp-select-node-type
                                v-model="item.nodeType"
                                v-model:text="item.nodeTypeName"
                                key-id="_id"
                                key-label="name"
                                :filterable="true"
                                :clearable="true"
                                :prop="'nodeType' + index"
                                :name="'nodeType' + index"
                                readonly />
                        </div>
                        <div class="flex justify-center">
                            <hi-pencil-square-icon
                                class="h-7 w-7 text-info-500 cursor-pointer"
                                @click="openWorkflowPosition(item)" />
                        </div>
                    </template>
                </hts-workflow-position>

                <p class="mb-4">
                    {{ $t('labels.theSamePositions') }}
                </p>

                <div
                    v-if="dataEdit.theSamePositions"
                    class="mb-3 text-center">
                    <div
                        v-for="(theSamePosition, index) in dataEdit.theSamePositions"
                        :key="index"
                        class="grid-cols-5 gap-4 grid min-w-full"
                        :class="{ 'mt-4': index !== 0 }">
                        <hts-form-select
                            v-model="dataEdit.theSamePositions[index][0]"
                            class="col-span-2"
                            :items="dataSelectTheSameNodes"
                            key-id="key"
                            key-label="value"
                            readonly
                            :color="color" />

                        <p class="text-3xl text-center mt-3">
                            =
                        </p>

                        <hts-form-select
                            v-model="dataEdit.theSamePositions[index][1]"
                            class="col-span-2"
                            :items="dataSelectTheSameNodes"
                            key-id="key"
                            key-label="value"
                            readonly
                            :color="color" />
                    </div>
                </div>
            </hts-card>

            <workflow-position
                ref="workflowPosition"
                disabled
                @update="(val) => updateWorkflowPosition(val)" />
        </div>
    </hts-drawer>
</template>
<script setup lang="ts">
import data from '../-imports/data'
import { IAddWorkflow, IWorkflowPosition } from '../-imports/form'
import WorkflowPosition from './workflow-position.vue'

import screen from '~/components/utils/screen'

const { transportation } = data()
const { $disableReactive } = useNuxtApp()

const breakpoint = screen()
const minWidthScroll = computed(() => {
  return `${breakpoint.value.thresholds.sm}px`
})

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: String,
    default: 'small'
  },
  sizeItemInput: {
    type: String,
    default: 'small'
  },
  column: {
    type: Number,
    default: 1
  }
})

const { color } = toRefs(props)

const visible = ref(false)

const emits = defineEmits(['close', 'refreshTables', 'newTask', 'update:show'])

const close = () => {
  emits('close')
}

const open = async (id: string) => {
  await getData(id)
  visible.value = true
}

const getData = async (id: string) => {
  const api = $urlAdmin.workflow.getByID
  const data: any = await $api(api, { uriParams: { ID: id } })
  const resData: any = data.data.value
  if (resData.success) {
    dataEdit.name = resData.data.name
    dataEdit.transportation = resData.data.transportation
    dataEdit.positionNumber = resData.data.positionNumber
    dataEdit.positions = Object.values(resData.data.positions)
    dataEdit.theSamePositions = resData.data.theSamePositions
  }
}

const dataEdit = reactive<IAddWorkflow>({
  name: '',
  transportation: '',
  positionNumber: null,
  positions: [],
  theSamePositions: []
})

const settingPositionNumber = ref()
const workflowPosition = ref()
const openSettingPositionNumber = () => {
  if (settingPositionNumber.value) {
    settingPositionNumber.value.open(dataEdit.positionNumber)
  }
}

const resetFields = () => {
  dataEdit.name = ''
  dataEdit.transportation = ''
  dataEdit.positionNumber = null
  dataEdit.positions = []
  dataEdit.theSamePositions = []
}

const openWorkflowPosition = (position: IWorkflowPosition) => {
  if (workflowPosition.value) {
    workflowPosition.value.open($disableReactive(position), dataEdit.transportation, true, false)
  }
}

const updateWorkflowPosition = (position: IWorkflowPosition) => {
  const index = position.index - 1
  dataEdit.positions[index] = position
}

const columnClass = computed(() => {
  if ($lodash.inRange(props.column, 1, 12)) {
    return `grid-cols-${props.column}`
  }

  return 'grid-cols-1'
})

const dataSelectTheSameNodes = computed(() => {
  if (!dataEdit.positions || !Array.isArray(dataEdit.positions)) {
    return []
  }

  return dataEdit.positions.map((item: any, index: number) => {
    return {
      key: index,
      value: `${$t('labels.position')} ${index + 1}`
    }
  })
})

const addTheSamePosition = () => {
  if (!dataEdit.theSamePositions) {
    dataEdit.theSamePositions = [[null, null]]
    return
  }

  dataEdit.theSamePositions.push([null, null])
}

const deleteTheSamePosition = (index: number) => {
  if (!dataEdit.theSamePositions || dataEdit.theSamePositions.length <= 1) {
    dataEdit.theSamePositions = [[null, null]]
    return
  }

  dataEdit?.theSamePositions?.splice(index, 1)
}

const clearBusinessAndTask = (index: number) => {
  dataEdit.positions[index].tasks = []
  dataEdit.positions[index].businesses = {}
}

defineExpose({
  open
})
</script>
